import { z } from "zod"

export const PIM_WORKFLOW_STATE = {
  queued: "queued",
  running: "running",
  retrying: "retrying",
  failed: "failed",
  complete: "complete",
  notApplicable: "not_applicable",
  blocked: "blocked",
  superseded: "superseded",
  unknown: "unknown",
} as const
export const pimWorkflowStateSchema = z.enum(Object.values(PIM_WORKFLOW_STATE))
export const pimWorkflowStageSchema = z.object({
  state: pimWorkflowStateSchema,
  reason: z.string().max(120).nullable(),
  recordedAt: z.string().nullable(),
  evidence: z.enum(["recorded", "partial", "expired", "unknown"]),
})
export const pimWorkflowCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  complete: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  unknown: z.number().int().nonnegative(),
  notApplicable: z.number().int().nonnegative(),
  superseded: z.number().int().nonnegative(),
})
export const pimWorkflowSummarySchema = z.object({
  historicalFailed: z.boolean().default(false),
  resolvedDeliveries: z.number().int().nonnegative().default(0),
  resolvedTargets: z.number().int().nonnegative().default(0),
  unresolvedTerminalTargets: z.number().int().nonnegative().default(0),
  scopes: z.number().int().nonnegative(),
  matchingScopes: z.number().int().nonnegative(),
  failed: z.boolean(),
  pending: z.boolean(),
  unknown: z.boolean(),
  generation: pimWorkflowCountsSchema,
  upload: pimWorkflowCountsSchema,
  current: pimWorkflowCountsSchema,
  publication: pimWorkflowCountsSchema,
})
export const pimWorkflowReceiptSchema = z.object({
  id: z.number().int(),
  receivedAt: z.string(),
  productId: z.number().int().nullable(),
  kind: z.string().nullable(),
  sku: z.string().nullable(),
  skuCount: z.number().int().nonnegative(),
  validation: z.enum(["accepted", "rejected"]),
  validationReason: z.string().max(120).nullable(),
  transportAttempts: z.number().int(),
  messageId: z.string().nullable().optional(),
  publishedAt: z.string().nullable().optional(),
  deliveries: z
    .array(
      z.object({
        id: z.number().int(),
        consumer: z.literal("product_visuals"),
        state: z.string(),
        processingAttempts: z.number().int().nonnegative(),
        nextRetryAt: z.string().nullable(),
        processedAt: z.string().nullable(),
      })
    )
    .optional(),
  coverage: z.enum(["instrumented", "partial", "expired"]),
  summary: pimWorkflowSummarySchema,
  deliveryResolution: z
    .object({
      id: z.string(),
      deliveryId: z.number().int().positive(),
      resolvedAt: z.string(),
    })
    .nullable()
    .default(null),
})
export const pimWorkflowScopeSchema = z.object({
  id: z.string(),
  targetId: z.number().int(),
  store: z.string(),
  frozenStore: z.string(),
  sku: z.string().nullable(),
  kind: z.string(),
  fillJobId: z.string().nullable(),
  workId: z.string().nullable(),
  resolutionId: z.string().nullable(),
  outboxId: z.number().int().nullable(),
  replacementId: z.string().nullable(),
  renderObservationId: z.string().nullable(),
  sharedReceiptCount: z.number().int(),
  recovery: z
    .object({ id: z.string(), state: z.string(), resolvedAt: z.string().nullable() })
    .nullable()
    .default(null),
  dispatch: pimWorkflowStageSchema,
  content: pimWorkflowStageSchema,
  generation: pimWorkflowStageSchema,
  upload: pimWorkflowStageSchema,
  current: pimWorkflowStageSchema,
  publication: pimWorkflowStageSchema,
})
export const pimWorkflowAttemptSchema = z.object({
  id: z.string(),
  category: z.enum([
    "fill",
    "content",
    "fanout",
    "replacement",
    "render",
    "transition",
    "publisher",
  ]),
  recordedAt: z.string(),
  store: z.string().nullable(),
  sku: z.string().nullable(),
  state: z.string(),
  reason: z.string().nullable(),
  parentId: z.string().nullable(),
  evidence: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
})
const cursorSchema = z
  .string()
  .max(100)
  .refine((value) => {
    const parts = value.split("|")
    return (
      parts.length === 2 &&
      z.string().datetime({ offset: true }).safeParse(parts[0]).success &&
      z.coerce.number().int().positive().max(2147483647).safeParse(parts[1]).success
    )
  }, "Invalid receipt cursor")
const attemptCursorSchema = z
  .string()
  .max(300)
  .refine((value) => {
    const parts = value.split("|")
    return (
      parts.length === 2 &&
      z.string().datetime({ offset: true }).safeParse(parts[0]).success &&
      /^[a-z]+:[a-zA-Z0-9-]+$/.test(parts[1] ?? "")
    )
  }, "Invalid attempt cursor")
export const pimWorkflowListQuerySchema = z
  .object({
    sku: z.string().trim().min(1).max(128).optional(),
    productId: z.coerce.number().int().positive().optional(),
    store: z.string().trim().min(1).max(64).optional(),
    kind: z.string().trim().min(1).max(64).optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    status: z.enum(["pending", "failed", "unknown"]).optional(),
    before: cursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .refine(
    (value) => !value.from || !value.to || Date.parse(value.from) <= Date.parse(value.to),
    "Received range is reversed"
  )
export const pimWorkflowDetailQuerySchema = z.object({
  store: z.string().min(1).max(64).optional(),
  sku: z.string().min(1).max(128).optional(),
  scopeId: z.string().min(1).max(300).optional(),
  targetStatus: z.literal("failed").optional(),
  targetAfter: z.string().max(300).optional(),
  attemptAfter: attemptCursorSchema.optional(),
  targetLimit: z.coerce.number().int().min(1).max(100).default(25),
  attemptLimit: z.coerce.number().int().min(1).max(100).default(25),
})
export const pimWorkflowListSchema = z.object({
  receipts: z.array(pimWorkflowReceiptSchema),
  nextCursor: z.string().nullable(),
  observedAt: z.string(),
  controls: z.object({
    generationEnabled: z.boolean().optional(),
    fillAvailable: z.boolean().optional(),
    renderAvailable: z.boolean().optional(),
    captureStartedAt: z.string().nullable(),
    dispatchPaused: z.boolean(),
    tracingActivatedAt: z.string().nullable(),
  }),
})
export const pimWorkflowBlockersSchema = z.object({
  scopeCount: z.number().int().nonnegative(),
  receiptRejected: z.boolean(),
  deliveryFailures: z.number().int().nonnegative(),
  scopes: z
    .array(
      z.object({
        id: z.string(),
        targetId: z.number().int(),
        store: z.string(),
        sku: z.string().nullable(),
        failures: z
          .array(
            z.object({
              stage: z.enum([
                "dispatch",
                "content",
                "generation",
                "upload",
                "current",
                "publication",
              ]),
              reason: z.string().max(120).nullable(),
            })
          )
          .max(6),
      })
    )
    .max(5),
})
export const pimWorkflowDetailSchema = z.object({
  blockers: pimWorkflowBlockersSchema,
  receipt: pimWorkflowReceiptSchema,
  targets: z.array(pimWorkflowScopeSchema),
  nextTargetCursor: z.string().nullable(),
  attempts: z.array(pimWorkflowAttemptSchema),
  nextAttemptCursor: z.string().nullable(),
  observedAt: z.string(),
})
export type PimWorkflowListQuery = z.infer<typeof pimWorkflowListQuerySchema>
export type PimWorkflowDetailQuery = z.infer<typeof pimWorkflowDetailQuerySchema>
export type PimWorkflowReceipt = z.infer<typeof pimWorkflowReceiptSchema>
export type PimWorkflowScope = z.infer<typeof pimWorkflowScopeSchema>
export type PimWorkflowList = z.infer<typeof pimWorkflowListSchema>
export type PimWorkflowDetail = z.infer<typeof pimWorkflowDetailSchema>

export type PimWorkflowRuntimeControls = {
  generationEnabled: boolean
  fillAvailable: boolean
  renderAvailable: boolean
}
